import { Box, Flex, Stack, VStack } from "@chakra-ui/react";
import { BreakerRoutes } from "appRoutePaths";
import { BreakerInfoBanner } from "components/breaker/BreakerInfoBanner";
import { CustomerInfoBanner } from "components/CustomerInfoBanner";
import { ImpersonatingBanner } from "components/ImpersonatingBanner";
import { Navigate } from "components/Navigate";
import { Footer } from "components/Navigation/Footer";
import { NavHeader } from "components/Navigation/NavHeader";
import { SideMenu } from "components/Navigation/SideMenu/SideMenu";
import { Roles } from "context/auth";
import { useSignalR } from "context/signalr";
import { useAuthCookiePersistence } from "hooks/useAuthCookiePersistence";
import { useContactDetailsRedirect } from "hooks/useContactDetails";
import useCustomerMenuItems from "hooks/useCustomerMenuItems";
import { FC, useEffect } from "react";
import { Outlet } from "react-router-dom";
import InAppLayout from "./InAppLayout";
import useIdentityRedirection from "./useIdentityRedirection";

enum InfoBannerType {
    Customer = 0,
    Breaker = 1,
}

const InfoBanner: FC<{ visible: boolean; type: InfoBannerType }> = ({
    visible,
    type,
}) => {
    if (!visible) {
        return null;
    } else if (type === InfoBannerType.Breaker) {
        return <BreakerInfoBanner />;
    }

    return <CustomerInfoBanner />;
};

const CustomerLayout = ({ children, bare = false }: any) => {
    const signalR = useSignalR();
    useContactDetailsRedirect();

    const { isInWebView } = useAuthCookiePersistence();
    const { shouldRedirect, destination, roles } = useIdentityRedirection([
        Roles.User,
        Roles.Breaker,
    ]);
    const menuItems = useCustomerMenuItems(roles, !bare);

    useEffect(() => {
        signalR.connect();
    }, [signalR]);

    if (shouldRedirect) {
        return <Navigate to={destination!} replace />;
    }

    // Page is loaded from within the mobile app, use the app-specific layout instead
    if (isInWebView) {
        return <InAppLayout>{children}</InAppLayout>;
    }

    // HACK: Not so bad, but much better ways exist to hide the <CustomerInfo /> on some pages (support)
    const isOnSupportPage = window.location.pathname.includes("support");
    const bannerType =
        roles.includes(Roles.Breaker) &&
        window.location.pathname.includes(BreakerRoutes.portal.url)
            ? InfoBannerType.Breaker
            : InfoBannerType.Customer;

    return (
        <Flex
            flexDir="column"
            w="100%"
            justifyContent="space-between"
            minH="100vh"
            backgroundColor="#1f2937"
            color="var(--dcs-white)"
        >
            <NavHeader />
            <ImpersonatingBanner />
            <Stack
                direction={{ base: "column", md: "row" }}
                align="start"
                spacing={0}
                width="100vw"
                flex={1}
                overflow="auto"
            >
                {bare === false && <SideMenu entries={menuItems} />}
                <VStack
                    alignItems="baseline"
                    w="100%"
                    h="100%"
                    position="relative"
                    flex={1}
                    minW={0}
                >
                    {bare === false && (
                        <InfoBanner
                            visible={!isOnSupportPage}
                            type={bannerType}
                        />
                    )}
                    <Box px={{ base: 5, md: 15 }} py={3} width="100%">
                        {children ? children : <Outlet />}
                    </Box>
                </VStack>
            </Stack>
            <Footer />
        </Flex>
    );
};

export default CustomerLayout;
